import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EdgeHandle } from '@components/ui/EdgeHandle/EdgeHandle';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { ZoomButtons } from '@components/ui/ZoomButtons/ZoomButtons';
import { useZoomSteps } from '@components/ui/ZoomButtons/useZoomSteps';
import { useStagePan } from '@components/Pages/LogisticsPage/panels/SiteMap/useStagePan';
import { ChevronDownIcon, RewindClockIcon } from '@utils/icons';
import { SpecialAreas } from '../MainView/SpecialAreas';
import { useBuildingCard } from '../MainView/useBuildingCard';
import { ReplayBar } from '../replay/ReplayBar';
import type { IReplayController } from '../replay/useReplayController';
import { useBuildingFit } from './useBuildingFit';
import mainViewStyles from '../MainView/mainView.module.css';
import styles from './fullscreen.module.css';

interface IFullscreenMainProps {
  replay: IReplayController;
}

/**
 * Fullscreen's building and special-area columns, and the replay bar floating
 * over the whole screen. The bar stays mounted when collapsed: it owns the
 * playback clock and the keyboard, and hands its play button to the building panel.
 */
function FullscreenMainInner({ replay }: IFullscreenMainProps) {
  const { t } = useTranslation();
  const zoomSteps = useZoomSteps();
  const { cardClassName, buildingClassName, cardLabel, building, failed } = useBuildingCard(replay, zoomSteps.zoom);
  const stageRef = useRef<HTMLDivElement>(null);
  const buildingRef = useRef<HTMLDivElement>(null);
  const [barCollapsed, setBarCollapsed] = useState(false);
  const [playSlot, setPlaySlot] = useState<HTMLElement | null>(null);
  const fit = useBuildingFit(stageRef, buildingRef);
  const { isPanning, isPannable } = useStagePan({ containerRef: stageRef, enabled: true, zoom: zoomSteps.zoom });
  const toggleBar = useCallback(() => setBarCollapsed((current) => !current), []);

  // Replay closed and reopened: the bar comes back in full.
  const { barProps } = replay;
  if (!barProps && barCollapsed) setBarCollapsed(false);

  return (
    <div className={`${mainViewStyles.mainView} ${mainViewStyles.fullscreenColumns}`}>
      <div className={styles.main}>
        <section className={`${cardClassName} ${styles.center}`} aria-label={cardLabel}>
          <ScrollArea
            orientation="both"
            className={styles.stage}
            viewportClassName={styles.stageViewport}
            viewportRef={stageRef}
            viewportProps={{ 'data-pan': isPanning ? 'panning' : isPannable ? 'pannable' : undefined }}
          >
            <div
              ref={buildingRef}
              className={buildingClassName}
              style={fit === undefined ? undefined : { width: fit * zoomSteps.zoom, flex: 'none' }}
            >
              {building}
            </div>
          </ScrollArea>
          <div className={styles.zoom}>
            <ZoomButtons
              zoom={zoomSteps}
              zoomOutLabel={t('monitor.fullscreen.zoomOut')}
              zoomInLabel={t('monitor.fullscreen.zoomIn')}
            />
          </div>
          <div ref={setPlaySlot} className={styles.playSlot} />
          {failed}
        </section>
        <ScrollArea className={styles.areas}>
          <SpecialAreas />
        </ScrollArea>
        {barProps && (
          <div className={styles.barDock} data-collapsed={barCollapsed}>
            <EdgeHandle
              className={styles.barHandle}
              aria-expanded={!barCollapsed}
              aria-label={t(barCollapsed ? 'monitor.fullscreen.expandReplay' : 'monitor.fullscreen.collapseReplay')}
              onClick={toggleBar}
            >
              {barCollapsed ? <RewindClockIcon /> : <ChevronDownIcon size={16} />}
            </EdgeHandle>
            <div className={styles.barInner} inert={barCollapsed}>
              <ReplayBar {...barProps} collapsedPlaySlot={barCollapsed ? playSlot : null} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Memoized: the page root re-renders on every replay tick; this only when its replay changes. */
export const FullscreenMain = memo(FullscreenMainInner);
